/**
 * AskStewie API client — structured answers only.
 * Uses the structured answer engine exclusively.
 */
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Ask a question through the structured answer engine.
 * Returns a rich structured response with rule text, citations, fix steps,
 * timing, rights protection, takeaways, and grievance-ready language.
 *
 * Every response (success or failure) returns the full structured schema.
 *
 * @param {string} question - The user's question
 * @param {object} [options] - Optional settings
 * @param {string} [options.token] - Auth token
 * @returns {Promise<object>} { ok, answer: StructuredAnswer }
 */
export async function askStewieStructured(question, options = {}) {
  const { token, userContext, sessionId } = options;
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const body = { question: question.trim() };
  if (userContext) body.user_context = userContext;
  if (sessionId) body.session_id = sessionId;

  const res = await axios.post(
    `${API}/api/ask-stewie/structured-answer`,
    body,
    { headers }
  );

  if (!res.data?.ok) {
    throw new Error('Unexpected response from structured answer engine');
  }

  return res.data;
}

/**
 * Format a citation string from structured fields.
 * Consistent format: "Article X | Section Y | Title | p. N"
 */
export function formatCitation(citation) {
  if (!citation) return '';
  const parts = [];
  if (citation.article) parts.push(`Article ${citation.article}`);
  if (citation.section) parts.push(`Section ${citation.section}`);
  if (citation.source_title) parts.push(citation.source_title);
  if (citation.page) parts.push(`p. ${citation.page}`);
  return parts.length > 0 ? parts.join(' | ') : citation.source_title || 'Source';
}

export default askStewieStructured;
